import defaultTheme from '../config/theme';

const FONT_STACKS = {
  inter: '"Inter", Arial, sans-serif',
  'open-sans': '"Open Sans", Arial, sans-serif',
  playfair: '"Playfair Display", Georgia, serif',
  georgia: 'Georgia, "Times New Roman", serif',
  lora: '"Lora", Georgia, serif',
  merriweather: '"Merriweather", Georgia, serif',
};

const FONT_GOOGLE_URLS = {
  inter: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap',
  'open-sans': 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap',
  playfair: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&display=swap',
  lora: 'https://fonts.googleapis.com/css2?family=Lora:wght@400;600;700&display=swap',
  merriweather: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap',
};

function resolveFont(key, fallback = 'inter') {
  return FONT_STACKS[key] || FONT_STACKS[fallback];
}

function ensureGoogleFont(fontKey) {
  const url = FONT_GOOGLE_URLS[fontKey];
  if (!url || fontKey === 'georgia') return;

  const id = `hh-font-${fontKey}`;
  if (document.getElementById(id)) return;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

/**
 * Applies CMS theme tokens as CSS variables on :root and returns merged styled-components theme.
 */
export function applySiteTheme(cmsTheme = {}) {
  const root = document.documentElement;

  const primaryColor =
    cmsTheme.customPrimaryColor || cmsTheme.primaryColor || defaultTheme.colors.primary;
  const bodyFontSize =
    cmsTheme.customBodyFontSize || cmsTheme.bodyFontSize || defaultTheme.typography.fontSize.body;

  const vars = {
    '--hh-primary': primaryColor,
    '--hh-primary-light': cmsTheme.primaryLightColor || defaultTheme.colors.primaryLight,
    '--hh-accent': cmsTheme.accentColor || defaultTheme.colors.accentYellow,
    '--hh-text-primary': cmsTheme.textPrimaryColor || defaultTheme.colors.text.primary,
    '--hh-text-secondary': cmsTheme.textSecondaryColor || defaultTheme.colors.text.secondary,
    '--hh-background': cmsTheme.backgroundColor || defaultTheme.colors.background,
    '--hh-card': cmsTheme.cardBackgroundColor || defaultTheme.colors.card,
    '--hh-border': defaultTheme.colors.border,
    '--hh-font-body': resolveFont(cmsTheme.bodyFont, 'inter'),
    '--hh-font-heading': resolveFont(cmsTheme.headingFont, 'playfair'),
    '--hh-font-size-body': bodyFontSize,
    '--hh-font-size-h1': cmsTheme.h1FontSize || defaultTheme.typography.fontSize.h1,
    '--hh-font-size-h2': cmsTheme.h2FontSize || defaultTheme.typography.fontSize.h2,
    '--hh-font-size-h3': cmsTheme.h3FontSize || defaultTheme.typography.fontSize.h3,
    '--hh-font-size-h4': cmsTheme.h4FontSize || defaultTheme.typography.fontSize.h4,
  };

  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  if (cmsTheme.bodyFont) ensureGoogleFont(cmsTheme.bodyFont);
  if (cmsTheme.headingFont) ensureGoogleFont(cmsTheme.headingFont);

  document.body.style.fontFamily = vars['--hh-font-body'];
  // Body text color is owned by global.css (#f5f6fa, sits on the dark gradient bg).
  // Don't override it here, or default-colored text washes out against that background.

  return {
    ...defaultTheme,
    colors: {
      ...defaultTheme.colors,
      primary: vars['--hh-primary'],
      primaryLight: vars['--hh-primary-light'],
      background: vars['--hh-background'],
      card: vars['--hh-card'],
      text: {
        ...defaultTheme.colors.text,
        primary: vars['--hh-text-primary'],
        secondary: vars['--hh-text-secondary'],
      },
    },
    typography: {
      ...defaultTheme.typography,
      fontFamily: {
        primary: vars['--hh-font-body'],
        secondary: vars['--hh-font-heading'],
      },
    },
  };
}
